// frontend/src/pages/FoodDatabasePage.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

// Food Details Modal Component
const FoodDetailsModal = ({ isOpen, onClose, food }) => {
  if (!isOpen || !food) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold">{food.name}</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">Nutrition Information</h3>
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-600">Calories:</span> <strong>{food.calories || '0'}</strong></div>
                <div><span className="text-gray-600">Protein:</span> <strong>{food.protein || '0'}g</strong></div>
                <div><span className="text-gray-600">Carbs:</span> <strong>{food.carbs || '0'}g</strong></div>
                <div><span className="text-gray-600">Fat:</span> <strong>{food.fat || '0'}g</strong></div>
                {food.servings && (
                  <div className="col-span-2">
                    <span className="text-gray-600">Servings:</span> <strong>{food.servings}</strong>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                  {food.meal_type}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  food.type === 'veg' ? 'bg-green-100 text-green-800' : 
                  food.type === 'non-veg' ? 'bg-red-100 text-red-800' : 
                  'bg-blue-100 text-blue-800'
                }`}>
                  {food.type}
                </span>
                {food.region && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                    {food.region}
                  </span>
                )}
                {food.category && (
                  <span className="px-3 py-1 bg-purple-100 rounded-full text-sm font-medium text-purple-800">
                    {food.category.replace('_', ' ')}
                  </span>
                )}
              </div>
            </div>
            
            {food.video_id && (
              <div className="md:col-span-2">
                <h3 className="font-semibold text-lg mb-2">Source</h3>
                <a 
                  href={`https://youtube.com/watch?v=${food.video_id}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  <span>▶️ Watch on YouTube</span>
                </a>
              </div>
            )}
          </div>

          {food.description && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Description</h3>
              <p className="text-gray-700">{food.description}</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {food.ingredients && food.ingredients.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-2">Ingredients</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {Array.isArray(food.ingredients) 
                    ? food.ingredients.map((ingredient, idx) => (
                        <li key={idx} className="text-gray-700">{ingredient}</li>
                      ))
                    : <li className="text-gray-700">No ingredients available</li>
                  }
                </ul>
              </div>
            )}

            {(food.instructions || food.recipe) && (
              <div>
                <h3 className="font-semibold text-lg mb-2">Instructions</h3>
                <div className="whitespace-pre-line text-gray-700">
                  {food.instructions || food.recipe}
                </div>
              </div>
            )}
          </div>

          {food.notes && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
              <h4 className="font-semibold text-yellow-800 mb-1">Notes</h4>
              <p className="text-yellow-700">{food.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ITEMS_PER_PAGE = 20;

const FoodDatabasePage = () => {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedFood, setSelectedFood] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    meal_type: '',
    type: '',
    region: ''
  });
  const { user } = useAuth();
  
  // Close modal function
  const closeModal = () => {
    setSelectedFood(null);
    document.body.style.overflow = 'auto';
  };
  
  // Open view modal
  const openViewModal = (food) => {
    setSelectedFood(food);
    document.body.style.overflow = 'hidden';
  };

  // Handle view details by opening the modal
  const handleViewDetails = (food) => {
    openViewModal(food);
  };

  useEffect(() => {
    fetchFoods();
  }, [currentPage, filters]);

  const fetchFoods = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        ...filters
      };

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/foods/database`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });

      setFoods(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching food items:', error);
      toast.error('Failed to load food items');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h4 className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>🍽️ Food Database</h4>
              <p className="body-medium" style={{ color: 'rgb(112, 112, 112)' }}>Browse and search our collection of delicious meals</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              >
                <option value="">All Categories</option>
                <option value="bread">Bread</option>
                <option value="south_indian">South Indian</option>
                <option value="north_indian">North Indian</option>
                <option value="rice">Rice</option>
                <option value="snack">Snack</option>
                <option value="dessert">Dessert</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                value={filters.meal_type}
                onChange={(e) => setFilters({...filters, meal_type: e.target.value})}
              >
                <option value="">All Meal Types</option>
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Food Type</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
              >
                <option value="">All Food Types</option>
                <option value="veg">Vegetarian</option>
                <option value="non-veg">Non-Vegetarian</option>
                <option value="vegan">Vegan</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
              >
                <option value="">All Regions</option>
                <option value="North">North Indian</option>
                <option value="South">South Indian</option>
                <option value="East">East Indian</option>
                <option value="West">West Indian</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ category: '', meal_type: '', type: '', region: '' })}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg text-sm transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Food Items Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-sm text-gray-600">Loading meals...</p>
            </div>
          ) : foods.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-2">No meals found</p>
              <p className="text-sm text-gray-500 mb-4">Try adjusting your filters or check back later</p>
              <button
                onClick={() => setFilters({ category: '', meal_type: '', type: '', region: '' })}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {foods.map((food) => (
                <div 
                  key={food.id} 
                  className="p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openViewModal(food)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900">{food.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      food.type === 'veg' ? 'bg-green-100 text-green-800' :
                      food.type === 'non-veg' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {food.type}
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span className="capitalize">{food.meal_type}</span>
                    {food.region && <span className="mx-1">•</span>}
                    <span>{food.region}</span>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center mb-4">
                    <div>
                      <div className="font-bold text-gray-900">{food.calories || '0'}</div>
                      <div className="text-xs text-gray-500">Cal</div>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{food.protein || '0'}g</div>
                      <div className="text-xs text-gray-500">Protein</div>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{food.carbs || '0'}g</div>
                      <div className="text-xs text-gray-500">Carbs</div>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{food.fat || '0'}g</div>
                      <div className="text-xs text-gray-500">Fat</div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewModal(food);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <span>View</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalItems > ITEMS_PER_PAGE && (
            <div className="mt-8 flex justify-center">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-purple-600 hover:bg-purple-50'}`}
                >
                  Previous
                </button>

                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} of {Math.ceil(totalItems / ITEMS_PER_PAGE)}
                </span>

                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage * ITEMS_PER_PAGE >= totalItems}
                  className={`px-4 py-2 rounded-lg text-sm ${currentPage * ITEMS_PER_PAGE >= totalItems ? 'text-gray-400 cursor-not-allowed' : 'text-purple-600 hover:bg-purple-50'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          {/* Food Details Modal */}
          <FoodDetailsModal 
            isOpen={!!selectedFood} 
            onClose={closeModal} 
            food={selectedFood} 
          />
        </div>
      </div>
    </div>
  );
};

export default FoodDatabasePage;